import React, {FC, ReactElement} from "react";
import {Alert, Button, FormControl, FormControlLabel, FormLabel, Grid, Radio, RadioGroup, TextField, Tooltip as MuiTooltip, Typography} from "@mui/material";
import LoadingButton from "../../../components/button/LoadingButton";
import {observer} from "mobx-react";
import {newEmployeeWizardState, WorkPermissionType} from "../../../store/NewEmployeeWizardState";
import YesNoRadioGroup from "../../../components/input/YesNoRadioGroup";
import {ControlDivider} from "./NewEmployeeWizard";
import makeStyles from "@mui/styles/makeStyles";
import {Trans, withTranslation} from "react-i18next";
import Tooltip from "../../../components/Tooltip";
import {trans} from "../../../locale/utils";

interface IProps {
    handleNext: () => void
}

const useStyles = makeStyles({
    euEaaCountriesText: {
        fontWeight: "bold",
        fontStyle: "italic",
        textDecoration: "underline"
    },
    addressInput: {},
});

const EU_EEA_COUNTRY_TIP = <>
    <Trans i18nKey="new_employee.personal_info.citizenship_question_tip">
        <Typography variant={"subtitle2"}>EU countries</Typography>
        Austria, Belgium, Bulgaria, Croatia, Republic of Cyprus, Czech Republic, Denmark, Estonia, Finland, France, Germany, Greece, Hungary,
        Ireland, Italy, Latvia, Lithuania, Luxembourg, Malta, Netherlands, Poland, Portugal, Romania, Slovakia, Slovenia, Spain and Sweden.
        <br/>
        <Typography variant={"subtitle2"}>EEA countries</Typography>
        EU countries + Iceland, Liechtenstein, Norway
    </Trans>
</>;

export const PersonalDetailsSection: FC<IProps> = observer(({handleNext}): ReactElement => {
    const classes = useStyles();
    const nextDisabled =
            newEmployeeWizardState.performsWorkInEstonia === null ||
            !newEmployeeWizardState.name ||
            !newEmployeeWizardState.surname ||
            !newEmployeeWizardState.personalCode ||
            newEmployeeWizardState.allowedToWorkByCitizenship === undefined ||
            (!newEmployeeWizardState.allowedToWorkByCitizenship &&
                    (!newEmployeeWizardState.workPermissionType || newEmployeeWizardState.workPermissionType === WorkPermissionType.DONT_KNOW))

    return <>
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <YesNoRadioGroup
                        required={true}
                        label={<Trans i18nKey="new_employee.personal_info.lives_in_estonia">Does employee performs duties living in Estonia?</Trans>}
                        value={newEmployeeWizardState.performsWorkInEstonia ?? null}
                        onChange={newValue => newEmployeeWizardState.performsWorkInEstonia = newValue}
                />
            </Grid>
            <Grid container item xs={12}>
                <Grid container item xs={12} md={5} spacing={1}>
                    <Grid item xs={6}>
                        <TextField
                                fullWidth
                                required
                                label={<Trans i18nKey="new_employee.personal_info.name">Employee name</Trans>}
                                placeholder="Smanta"
                                value={newEmployeeWizardState.name ?? ""}
                                onChange={e => newEmployeeWizardState.name = e.target.value}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                                fullWidth
                                required
                                label={<Trans i18nKey="new_employee.personal_info.surname">Surname</Trans>}
                                placeholder={"Smith"}
                                value={newEmployeeWizardState.surname ?? ""}
                                onChange={e => newEmployeeWizardState.surname = e.target.value}
                        />
                    </Grid>
                </Grid>
            </Grid>
            <Grid item container xs={12}>
                <Grid item xs={12} md={5}>
                    <TextField
                            fullWidth
                            required
                            label={<Trans i18nKey="new_employee.personal_info.personal_code">Personal code</Trans>}
                            placeholder={"49002124277"}
                            value={newEmployeeWizardState.personalCode ?? ""}
                            onChange={e => newEmployeeWizardState.personalCode = e.target.value}
                    />
                </Grid>
            </Grid>
            <Grid item container xs={12}>
                <Grid item xs={12} md={5}>
                    <TextField
                            className={classes.addressInput}
                            fullWidth
                            required
                            label={<Trans i18nKey="new_employee.personal_info.residential_address">Residential address</Trans>}
                            placeholder={"Lossi plats 1a-1, 15165, Tallinn, Estonia"}
                            value={newEmployeeWizardState.residentialAddress ?? ""}
                            onChange={e => newEmployeeWizardState.residentialAddress = e.target.value}
                    />
                </Grid>
            </Grid>
            <Grid item container xs={12} spacing={2} alignItems={"center"}>
                <Grid item>
                    <YesNoRadioGroup
                            required={true}
                            label={<>
                                <Trans i18nKey="new_employee.personal_info.citizenship_question">
                                    Worker has <span className={classes.euEaaCountriesText}>Estonian</span> citizenship, or
                                    <span className={classes.euEaaCountriesText}>EU/EEA</span> country
                                    citizen, or Switzerland citizen
                                </Trans>
                            </>}
                            value={newEmployeeWizardState.allowedToWorkByCitizenship ?? null}
                            onChange={newValue => {
                                newEmployeeWizardState.allowedToWorkByCitizenship = newValue
                                newEmployeeWizardState.workPermissionType = undefined
                                newEmployeeWizardState.document = undefined
                                newEmployeeWizardState.workPermitDocument = undefined
                            }}
                    />
                </Grid>
                <Grid item>
                    <Tooltip variant="subtitle1">
                        {EU_EEA_COUNTRY_TIP}
                    </Tooltip>
                </Grid>
            </Grid>
            {newEmployeeWizardState.allowedToWorkByCitizenship === false && <>
                <Grid item xs={12}>
                    <FormControl>
                        <FormLabel required><Trans i18nKey="new_employee.personal_info.has_right_to_work">Does employee have right to work in Estonia?</Trans></FormLabel>
                        <RadioGroup row value={newEmployeeWizardState.workPermissionType ?? ""}
                                    onChange={e => {
                                        // @ts-ignore
                                        newEmployeeWizardState.workPermissionType = e.target.value;
                                    }}>
                            <FormControlLabel value={WorkPermissionType.PERM_RESIDENCE_PERMIT} control={<Radio/>}
                                              label={trans("new_employee.personal_info.right_to_work_perm_permit", "Yes, based on permanent residence permit")}/>
                            <FormControlLabel value={WorkPermissionType.TEMP_RESIDENCE_PERMIT} control={<Radio/>}
                                              label={trans("new_employee.personal_info.right_to_work_temp_permit", "Yes, based on temporary residence permit")}/>
                            <FormControlLabel value={WorkPermissionType.SHORT_TERM_PERMISSION} control={<Radio/>}
                                              label={trans("new_employee.personal_info.right_to_work_short_term_permit", "Yes, based on short term work permit")}/>
                            <FormControlLabel value={WorkPermissionType.DONT_KNOW} control={<Radio/>}
                                              label={trans("new_employee.personal_info.right_to_work_unknown", "Don't know")}/>
                        </RadioGroup>
                    </FormControl>
                </Grid>

                {newEmployeeWizardState.workPermissionType === WorkPermissionType.DONT_KNOW && <>
                    <Alert severity="error">
                        <Typography variant="body2">
                            <Trans i18nKey="new_employee.personal_info.ask_employee_status">
                                In case you don't know if the employee has the right to work in Estonia - you should address this question with your future staff member
                            </Trans>
                        </Typography>
                        <Typography variant="body2">
                            <Trans i18nKey="new_employee.personal_info.employee_should_know_status">
                                Employee should know answer to this question.
                            </Trans>
                        </Typography>
                        <Typography variant="subtitle2">
                            <u>
                                <Trans i18nKey="new_employee.personal_info.cant_hire">
                                    If foreign employee does not know own work permission status - you can't hire the worker.
                                </Trans>
                            </u>
                        </Typography>
                    </Alert>
                </>}
            </>}
        </Grid>
        <ControlDivider/>
        <Grid container spacing={1}>
            <Grid item>
                <MuiTooltip title={<Trans i18nKey="new_employee.personal_info.clear_data">Clears previously entered data</Trans>}>
                    <Button variant="contained" color="secondary" onClick={() => newEmployeeWizardState.resetState()}><Trans i18nKey="common.clear">Clear</Trans></Button>
                </MuiTooltip>
            </Grid>
            <Grid item>
                <LoadingButton disabled={nextDisabled} variant="contained" color="primary" onClick={handleNext}>
                    <Trans i18nKey="common.next">Next</Trans>
                </LoadingButton>
            </Grid>
        </Grid>
    </>
});

export default withTranslation()(PersonalDetailsSection);